import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { z } from 'zod';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';

import { getAppointmentTenantInfoQuery } from '../operations';
import { CommandProviderState } from '../types';
import { useProvisionAndNavigate } from '../useProvisionAndNavigate';

const uuidSchema = z.string().uuid();

export const useAppointmentResults = (query: string): CommandProviderState => {
  const trimmed = query.trim();
  const isUuid = uuidSchema.safeParse(trimmed).success;

  const { run, isMember } = useProvisionAndNavigate();

  const { data, loading } = useQuery(getAppointmentTenantInfoQuery, {
    skip: !isUuid,
    context: eluveAdminHasuraContext,
    variables: { appointmentId: trimmed },
  });

  const results = useMemo(() => {
    if (!isUuid) return [];
    const appointment = data?.appointments?.[0];
    if (!appointment?.tenantId) return [];

    const tenantName = appointment.tenant?.name ?? 'Unknown tenant';
    const tenantId = appointment.tenantId;
    const targetPath = `/admin/organizations/${tenantId}/appointments/${appointment.id}`;

    return [
      {
        id: `appointment:${appointment.id}`,
        label: `Appointment in ${tenantName}`,
        description: appointment.id,
        icon: 'FileEdit' as const,
        onSelect: () => run({ tenantId, targetPath }),
        confirmation: isMember(tenantId)
          ? undefined
          : {
              title: "You don't have access to this organization:",
              detail: tenantName,
              confirmLabel: 'Provision and open',
            },
      },
    ];
  }, [data, isUuid, isMember, run]);

  return { results, loading };
};
